import { Menu } from 'antd';
import React, { useMemo } from 'react';

import { useLocation, useNavigate, Routes, Route, Navigate } from 'react-router-dom';

import CCSConfiguration from '@components/ccs-configuration';

import ConfigureNetworkPanel from '@src/components/configure-network-panel';
import { trackManagementTabsClick } from '@src/sensor';
import {
  BlockEventsManagement,
  BlockVideoEventsManagement,
} from '@src/emp-3.0/components/SensorRemoteControl';

import { EEventSensorRemoteControlTabsType } from '@src/common/constant/enum';
import { sensorRemoteControlRouteMap } from '@src/pages/sensor-remote-control/constant';
import styles from './index.less';
import { useInternalAdmin } from '@hooks';
import ClickTracking from '@src/emp-3.0/components/SensorRemoteControl/ClickTracking';
import SessionReplay from '@src/emp-3.0/components/SensorRemoteControl/SessionReplay';
import IntelligentCollection from '@src/emp-3.0/components/SensorRemoteControl/IntelligentCollection';
import { useFeatureFlags } from '@hooks/useFeatureFlags';

const NewTag = () => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 18,
      padding: '2px 6px',
      borderRadius: 4,
      backgroundColor: 'rgba(82, 196, 26, 1)', // green
      color: '#fff',
      fontSize: 12,
      fontWeight: 500,
      lineHeight: '14px',
      opacity: 1,
    }}
  >
    New
  </span>
);

export const SensorRemoteControl = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const internalAdmin = useInternalAdmin();
  const { pathname } = location;
  const { isSessionReplayEnabled } = useFeatureFlags();

  const menuItems = useMemo(() => {
    const items = [
      {
        label: <span data-test-id="blockedEventsTab">Blocked Events</span>,
        key: sensorRemoteControlRouteMap[EEventSensorRemoteControlTabsType.BLOCKED_EVENT],
      },
      internalAdmin && {
        label: <span data-test-id="blockedVideoEventsTab">Blocked Video Events</span>,
        key: sensorRemoteControlRouteMap[EEventSensorRemoteControlTabsType.BLOCKED_VIDEO_EVENT],
      },
      internalAdmin && {
        label: <span data-test-id="ccsConfigurationTab">CCS Configuration</span>,
        key: sensorRemoteControlRouteMap[EEventSensorRemoteControlTabsType.CCS_CONFIGURATION],
      },
      {
        label: <span data-test-id="configuredNetworkRequestTab">Configured Network Requests</span>,
        key: sensorRemoteControlRouteMap[
          EEventSensorRemoteControlTabsType.NETWORK_REQUEST_CONFIGURE
        ],
      },
      {
        label: (
          <span
            data-test-id="clickTrackingTab"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            Auto Collection of Clicks
          </span>
        ),
        key: sensorRemoteControlRouteMap[EEventSensorRemoteControlTabsType.CLICK_TRACKING],
      },
      isSessionReplayEnabled && {
        label: (
          <span
            data-test-id="sessionReplayTab"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>
              Cohort Replay
            </span>
          </span>
        ),
        key: sensorRemoteControlRouteMap[EEventSensorRemoteControlTabsType.SESSION_REPLAY],
      },
      {
        label: (
          <span
            data-test-id="intelligentCollectionTab"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              lineHeight: '1.2',
              fontSize: '14px',
            }}
          >
            <span style={{ lineHeight: '1.2' }}>
              Intelligent Collection for <br /> Network Requests
            </span>
          </span>
        ),
        key: sensorRemoteControlRouteMap[EEventSensorRemoteControlTabsType.INTELLIGENT_COLLECTION],
      },
    ];
    return items.filter(Boolean);
  }, [internalAdmin, isSessionReplayEnabled]);

  return (
    <div className={styles.rootContainer}>
      <div className={styles.titlePanel}>
        <span className={styles.titleHeader} data-test-id="sensorRemoteConfigHeader">
          Sensor Remote Control
        </span>

        <span className={styles.titletext}>
        Flexible Control DPI sensors with precision. Enable or disable DPI features as
        needed to focus on ingested relevant data.
        </span>
      </div>

      <div className={styles.contentContainer} style={{ display: 'flex' }}>
        <Menu
          selectedKeys={[pathname]}
          mode="inline"
          items={menuItems}
          style={{ width: 292 }}
          onClick={({ key }) => {
            trackManagementTabsClick({ activeKey: key });
            navigate(key);
          }}
        />

        <div style={{ flex: 1, paddingLeft: 4 }}>
          <Routes>
            <Route
              path="/"
              element={
                <Navigate
                  to={sensorRemoteControlRouteMap[EEventSensorRemoteControlTabsType.BLOCKED_EVENT]}
                  replace
                />
              }
            />
            <Route path="/blocked-events" element={<BlockEventsManagement />} />
            {internalAdmin && (
              <Route path="/blocked-video-events" element={<BlockVideoEventsManagement />} />
            )}
            {internalAdmin && <Route path="/css-configuration" element={<CCSConfiguration />} />}
            <Route path="/configured-network-request" element={<ConfigureNetworkPanel />} />
            <Route path="/click-tracking" element={<ClickTracking />} />
            <Route path="/session-replay" element={<SessionReplay />} />
            <Route path="/intelligent-collection" element={<IntelligentCollection />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};
